export interface SecureChannelConfig {
  inviteToken: string;
  /** Optional enrollment framework assertion; omitted by legacy clients. */
  expectedFramework?: string;
  dataDir: string;
  apiUrl: string;
  agentName?: string;
  agentVersion?: string;
  /**
   * Build string this CLIENT reports on connect (#836), e.g.
   * `@agentvault/claude-bridge@0.7.12`. Distinct from `agentVersion`, which is
   * the AI agent's own version (OTel `gen_ai.agent.version`).
   *
   * Hosts that bundle this package's dist should set it so operators see the
   * build they installed rather than the inner plugin version. Defaults to
   * `@agentvault/agentvault@<version>`. Diagnostic only — nothing branches on it.
   */
  clientVersion?: string;
  platform?: string;
  maxHistorySize?: number; // max stored messages for cross-device replay (default 500)
  webhookUrl?: string; // URL to register for webhook notifications from backend
  /**
   * Local HTTP port (127.0.0.1 only) for proactive sends — POST /send,
   * /decision, /action, GET /status, /targets. When set, start() opens it once
   * the channel is ready (equivalent to calling startHttpServer(port)).
   * Default: none.
   */
  httpPort?: number;
  onMessage?: (plaintext: string, metadata: MessageMetadata) => void;
  onStateChange?: (state: ChannelState) => void;
  onA2AMessage?: (msg: A2AMessage) => void;
  /** Called when an A2A channel becomes ready (approved or activated). */
  onA2AChannelReady?: (info: {
    channelId: string;
    peerHubAddress: string;
    role: "creator" | "member";
    conversationId: string;
    topic?: string;
  }) => void;
  /** Enable client-side policy scanning. If true, rules are fetched on connect. */
  enableScanning?: boolean;
  /** If set, auto-backup encrypted state to server after state changes. */
  backupCode?: string;
  /**
   * When true, ignore any existing credentials in dataDir and perform a fresh
   * enrollment from the invite token. Used by `setup --force` to recover from
   * expired/corrupt credentials.
   *
   * Default: false (load existing credentials if present).
   *
   * Spec: docs/superpowers/specs/2026-05-25-device-credential-lifecycle-design.md §8.2
   */
  forceReEnroll?: boolean;
}

export type ChannelState =
  | "idle"
  | "enrolling"
  | "polling"
  | "activating"
  | "connecting"
  | "ready"
  | "disconnected"
  | "error";

export interface AttachmentData {
  filename: string;
  mime: string;
  size: number;
  filePath: string;
  /** Base64-encoded decrypted content (for images passed to LLM vision) */
  base64?: string;
  /** Extracted text content (for text-based files) */
  textContent?: string;
}

export interface MessageMetadata {
  messageId: string;
  /**
   * The 1:1 conversation the message came from. #1418: ABSENT for an MLS
   * message on a group no conversation is mapped to yet — it used to be
   * stamped with the PRIMARY conversation, which handed the reply to the
   * primary person. `conversationGroupId` still says where it came from.
   */
  conversationId?: string;
  /**
   * #1418 — the 1:1 group this message DECRYPTED under (MLS only). Attested by
   * the decrypt, so it outranks the local conversation→group map: pass it to
   * `runForConversation(conversationId, fn, { groupId })`.
   */
  conversationGroupId?: string;
  timestamp: string;
  topicId?: string;
  attachment?: AttachmentData;
  spanId?: string;
  parentSpanId?: string;
  messageType?: string;
  priority?: string;
  envelopeVersion?: string;
  /** Set when the message originated from a multi-agent room */
  roomId?: string;
  /**
   * The owner's answer to a decision_request this message carries (an option
   * selected, or action defer / cancel). Set by every 1:1 receive path;
   * `messageType` is then "decision_response". waitForDecision() reads it.
   */
  decision?: DecisionResponse;
  /**
   * #621 — POSITIVE assertion that this message decrypted under the owner's 1:1
   * material: an MLS group found in the `1to1-group:` map, or a Double Ratchet
   * session. The bridge's full-access DM lane requires this rather than
   * inferring 1:1 from the ABSENCE of `roomId`, so a future emit site that
   * forgets to set it lands on the locked listener instead of an open shell.
   */
  ownerDm1to1?: boolean;
  /** Device ID of the sender (room messages only) */
  senderDeviceId?: string;
  /** Whether the sender is an agent (vs owner) — room messages only */
  senderIsAgent?: boolean;
  /**
   * #1459 — the sender is a human in THIS agent's own account (room messages
   * only). `senderIsAgent === false` says only "a human"; a room can hold
   * another account's people. Gate owner-only power on this.
   */
  senderIsOwner?: boolean;
  /**
   * #1459 — this room turn carries a file (its content, or a pointer to it)
   * sent by someone other than this agent's owner. Owner-only power must not
   * run on such a turn even when `senderIsOwner` is true.
   */
  foreignContent?: boolean;
  /** Display name of the sender (room messages only) */
  senderName?: string;
  /** Human-readable room name (room messages only) */
  roomName?: string;
  /**
   * #1223 — the text the sender TYPED, before any attachment was folded into
   * the plaintext (`_augmentWithAttachment` prepends a banner + the file's
   * contents). The @ gate must read THIS, never the folded text: a pasted chat
   * log containing "@wren" is not an address. Room messages only; absent on
   * emitters older than 0.23.42, so consumers fall back to the plaintext.
   */
  caption?: string;
  /** Set when this message is a coordinator task (AES job dispatch) */
  coordinatorTask?: boolean;
  /** Coordinator job ID */
  jobId?: string;
  /** Coordinator task ID */
  taskId?: string;
  /** Coordinator job phase */
  phase?: string;
  /** Coordinator task timeout in seconds */
  timeoutSec?: number;
}

/**
 * Who an UNTARGETED owner message reaches (#1418).
 *
 * - `"person"` (the default): only the person involved — the person whose
 *   conversation triggered it. Every 1:1 of that person (their browsers share
 *   one group since #1399) receives it; nobody else in the account does.
 * - `"everyone"`: every person this agent has a 1:1 with. The ONLY way to reach
 *   them all, so it must be chosen explicitly and visibly by the caller.
 *
 * Ignored when `conversationId` names a conversation: that already says who.
 */
export type OwnerAudience = "person" | "everyone";

export interface SendOptions {
  /** Target a specific conversation. Without one the message goes to the
   *  person involved (see `audience`), never to every session. */
  conversationId?: string;
  /** #1418: who an untargeted message reaches. Default: the person involved. */
  audience?: OwnerAudience;
  topicId?: string;
  messageType?: string;
  priority?: string;
  parentSpanId?: string;
  metadata?: Record<string, unknown>;
}

export interface DecisionOption {
  option_id: string;
  label: string;
  risk_level: "low" | "medium" | "high" | "critical";
  is_default?: boolean;
}

export interface ContextRef {
  type: string;
  uri: string;
  label: string;
}

export interface DecisionRequest {
  title: string;
  description?: string;
  options: DecisionOption[];
  context_refs?: ContextRef[];
  deadline?: string; // ISO 8601
  auto_action?: {
    option_id: string;
    trigger: string;
    description?: string;
  };
}

export interface DecisionResponse {
  decision_id: string;
  selected_option_id?: string;
  resolved_at?: string; // ISO 8601
  action?: "approve" | "deny" | "defer" | "cancel";
  defer_until?: string;
  defer_reason?: string;
  /** Free-text reason the owner gave (cancel). */
  reason?: string;
}

export interface HistoryEntry {
  id: string;
  sender: "owner" | "agent";
  text: string;
  ts: string; // ISO 8601
  topicId?: string; // topic this message belongs to
}

export interface DeviceSession {
  ownerDeviceId: string;
  ratchetState: string; // serialized DoubleRatchet
  activated?: boolean; // true after first owner message received
  epoch?: number; // ratchet generation — incremented on each resync/rekey
}

export interface PersistedState {
  deviceId: string;
  deviceJwt: string;
  primaryConversationId: string;
  sessions: Record<string, DeviceSession>; // conversationId -> session
  identityKeypair: { publicKey: string; privateKey: string }; // hex
  ephemeralKeypair: { publicKey: string; privateKey: string }; // hex
  fingerprint: string;
  lastMessageTimestamp?: string; // ISO timestamp of last received message
  messageHistory?: HistoryEntry[]; // stored messages for cross-device replay
  historyBackupSalt?: string;
  restoredHistoryGenerationId?: string;
  historyRecoveryState?: "pending" | "complete";
  /**
   * Topics this agent created or listed. `groupId` names the conversation
   * group the topic belongs to (#1416); entries written before #1416 carry
   * none and belong to the primary group (`groupId` below).
   */
  topics?: Array<{ id: string; name: string; isDefault: boolean; groupId?: string }>;
  /**
   * The PRIMARY group's default topic. Kept for older plugin versions and
   * backups; since #1416 the per-group map below is the source of truth, and
   * this field always mirrors `defaultTopicIds[groupId]`.
   */
  defaultTopicId?: string;
  /**
   * #1416: default topic per conversation group (conversation group id ->
   * topic id). Since #1399 every person has their own 1:1 group with the
   * agent, and a topic belongs to exactly one group — a frame into person B's
   * group must never carry person A's topic.
   */
  defaultTopicIds?: Record<string, string>;
  groupId?: string;
  hubAddress?: string;
  hubId?: string; // hub UUID — used for telemetry ingestion
  agentHubId?: string; // agent's hub_id (alias of hubId for routing clarity)
  agentRole?: "lead" | "peer"; // lead agent uses root workspace dir
  ownerHubId?: string; // owner's hub_id (populated from device_linked or enrollment)
  /** A2A channel state (agent-to-agent communication) */
  a2aChannels?: Record<string, {
    channelId: string;
    conversationId: string;
    role?: "creator" | "member";
    topic?: string;
    mlsGroupId?: string;
    participants: Array<{
      hubId: string;
      hubAddress: string;
      status: "invited" | "active" | "left" | "removed";
      /**
       * Backend device UUID for this participant.
       * Required for MLS Welcome dispatch (backend ws.py mls_welcome handler
       * routes by target_device_id). Optional/null on legacy persisted state
       * from before this field was added; next a2a_channel_approved event
       * repopulates it.
       */
      deviceId?: string | null;
    }>;
  }>;
  /** Outbound messages queued while disconnected */
  outboundQueue?: Array<{
    convId: string;
    headerBlob: string;
    ciphertext: string;
    messageGroupId: string;
    topicId?: string;
    /** Deferred plaintext capture, published to history only after relay. */
    history?: {
      id: string;
      text: string;
      topicId: string;
    };
  }>;
  /** Multi-agent room state */
  rooms?: Record<string, RoomState>;
  /** Last inbound room ID for sticky room routing across restarts */
  lastInboundRoomId?: string;
  /** Persisted message dedup IDs — survives restarts to prevent double-decrypt */
  seenMessageIds?: string[];
  /** Persisted A2A message dedup IDs */
  seenA2AMessageIds?: string[];
  /** Shadow mode skill configurations (synced from backend via WS) */
  shadowSkills?: Record<string, {
    sessionId: string;
    autonomyLevel: "shadow" | "supervised" | "autonomous";
    decisionClass: string;
  }>;
  /** MLS group state for 1:1 conversations (conversationId -> {mlsGroupId}) */
  mlsConversations?: Record<string, { mlsGroupId: string }>;
  /** Shared MLS group state for 1:1 multi-device (conversationGroupId -> {mlsGroupId, memberDeviceIds}) */
  mlsGroups?: Record<string, {
    mlsGroupId: string;
    memberDeviceIds?: string[];
  }>;
  /** Maps conversationId → conversation.group_id for shared MLS group lookup on reconnect */
  conversationGroupIds?: Record<string, string>;
}

/** Legacy state format for backward compatibility */
export interface LegacyPersistedState {
  deviceId: string;
  deviceJwt: string;
  conversationId: string;
  identityKeypair: { publicKey: string; privateKey: string }; // hex
  ephemeralKeypair: { publicKey: string; privateKey: string }; // hex
  fingerprint: string;
  ratchetState: string; // serialized DoubleRatchet
  lastMessageTimestamp?: string;
}

// --- Room types (multi-agent rooms) ---

export interface RoomMemberInfo {
  deviceId: string;
  entityType: string;
  displayName: string;
  identityPublicKey?: string;
  ephemeralPublicKey?: string;
  /** #1459 — the member device's own account, as the server reports it. */
  tenantId?: string;
}

export interface RoomConversationInfo {
  id: string;
  participantA: string;
  participantB: string;
}

export interface RoomInfo {
  roomId: string;
  name: string;
  members: RoomMemberInfo[];
  conversationIds: string[];
}

export interface RoomState {
  roomId: string;
  name: string;
  conversationIds: string[];
  members: RoomMemberInfo[];
  /** MLS group identifier (set when room uses MLS encryption) */
  mlsGroupId?: string;
  /** ISO timestamp of last MLS message received in this room (for sync catch-up) */
  lastMlsMessageTs?: string;
}

export interface HeartbeatStatus {
  agent_status: string;
  current_task: string;
}

export interface StatusAlert {
  title: string;
  message: string;
  severity: "info" | "warning" | "error" | "critical";
  detail?: string;
  detailFormat?: "markdown" | "json" | "text";
  category?: "performance" | "security" | "error" | "info";
}

// --- Room participant event types (team room join/leave) ---

export interface RoomParticipantEvent {
  roomId: string;
  participant: {
    deviceId: string;
    participantType: "human" | "agent";
    displayName?: string;
  };
}

// --- A2A Channel types (agent-to-agent communication) ---

export interface A2AChannelParticipant {
  id: string;
  hub_id: string;
  hub_address: string;
  agent_name: string;
  tenant_id: string;
  device_id: string | null;
  role: "creator" | "member";
  status: "invited" | "active" | "left" | "removed";
  observer_enabled: boolean;
  joined_at: string | null;
  left_at: string | null;
}

export interface A2AChannel {
  channelId: string;
  creator_hub_id: string;
  conversationId: string | null;
  status: "pending" | "approved" | "active" | "rejected" | "revoked";
  createdAt: string;
  max_participants: number;
  participant_count: number;
  participants: A2AChannelParticipant[];
  topic?: string;
}

export interface A2AMessage {
  text: string;
  fromHubAddress: string;
  channelId: string;
  conversationId: string;
  parentSpanId?: string;
  timestamp: string;
}

// --- Unified Delivery Protocol types ---

/** Explicit routing target for outbound messages. */
export type DeliveryTarget =
  | { kind: "owner" }
  | { kind: "room"; roomId: string }
  | { kind: "a2a"; hubAddress: string }
  | { kind: "a2a_channel"; channelId: string }
  | { kind: "context" }; // Resolve from _lastInboundRoomId (opt-in only)

export interface ShadowRecommendation {
  sessionId: string;
  skillName: string;
  decisionClass: string;
  recommendedAction: string;
  reasoningTrace?: string;
  observationId: string;
}

/** Structured message content for the deliver() dispatcher. */
export type DeliveryContent =
  | { type: "text"; text: string }
  | { type: "decision_request"; request: DecisionRequest }
  | { type: "status_alert"; alert: StatusAlert }
  | { type: "action_confirmation"; confirmation: ActionConfirmation }
  | { type: "artifact"; artifact: ArtifactPayload }
  | { type: "attachment"; text: string; filePath: string }
  | { type: "policy_alert"; alert: PolicyAlert }
  | { type: "approval_request"; request: ApprovalRequest }
  | { type: "approval_response"; response: ApprovalResponse }
  | { type: "shadow_recommendation"; recommendation: ShadowRecommendation };

export interface ActionConfirmation {
  action: string;
  status: "completed" | "failed" | "partial";
  decisionId?: string;
  detail?: string;
  estimated_cost?: number;
}

export interface ArtifactPayload {
  filePath: string;
  filename: string;
  mimeType: string;
  description?: string;
}

/** Notify agent owner of a policy violation. */
export interface PolicyAlert {
  ruleId: string;
  scope: "tool" | "model" | "rate" | "network" | "custom";
  action: "block" | "warn" | "log";
  violationType: string;
  message: string;
  skillName?: string;
  toolName?: string;
  model?: string;
  timestamp?: string;
}

/** Human-in-the-loop gate for sensitive operations. */
export interface ApprovalRequest {
  approvalId: string;
  category: string;
  title: string;
  description: string;
  details?: Record<string, unknown>;
  expiresAt?: string;
  requiredTier?: string;
}

/** Owner response to an approval request. */
export interface ApprovalResponse {
  approvalId: string;
  approved: boolean;
  reason?: string;
  conditions?: string[];
  signature?: string;     // Ed25519 signed approval artifact
}

// ---------------------------------------------------------------------------
// Credential Delivery Protocol types
// ---------------------------------------------------------------------------

export interface CredentialGrantPayload {
  type: "credential_grant";
  agreement_id: string;
  credentials: Array<{
    key: string;
    value: string;
    type: string;     // "api_key", "oauth_token", etc.
    scope: string;    // "read", "write", "admin"
  }>;
  timestamp: string;
  nonce: string;
}

export interface CredentialRevokePayload {
  type: "credential_revoke";
  agreement_id: string;
  credential_keys: string[];
  reason?: string;
  timestamp: string;
}

export interface CredentialAckPayload {
  type: "credential_ack";
  agreement_id: string;
  acknowledged_keys: string[];
  status: "stored" | "revoked";
  timestamp: string;
}

export interface CredentialRequestPayload {
  type: "credential_request";
  agreement_id: string;
  requested_keys: string[];
  reason?: string;
  timestamp: string;
}

export interface DeliveryOptions {
  conversationId?: string;
  /** #1418: who an untargeted owner delivery reaches (default: the person involved). */
  audience?: OwnerAudience;
  /**
   * #1418: refuse (receipt `ambiguous: true`) instead of guessing when the
   * delivery resolves to the owner, names nobody, and more than one person is
   * mid-turn. For callers with no turn of their own (the local HTTP API);
   * without it the message goes to the primary person, as heartbeats and
   * schedules expect.
   */
  refuseIfAmbiguous?: boolean;
  topicId?: string;
  priority?: string;
  parentSpanId?: string;
  metadata?: Record<string, unknown>;
}

export interface DeliveryReceipt {
  ok: boolean;
  destination: { kind: "owner" | "room" | "a2a"; id?: string };
  error?: string;
  queued?: boolean;
  decisionId?: string;
  /** #1418: refused under `refuseIfAmbiguous` — say which conversation, or `audience: "everyone"`. */
  ambiguous?: boolean;
  /**
   * True when this call was collapsed by the outbound idempotency guard —
   * an exact-duplicate of a message just sent to the same destination+topic
   * within the dedupe window (#475). The prior send stands; nothing was sent
   * again. ok is still true (the message reached the owner via the first send).
   */
  deduped?: boolean;
  timestamp: string;
}

export interface TargetInfo {
  kind: "owner" | "room" | "a2a";
  id: string;
  label: string;
  available: boolean;
}

/**
 * Parse a string target into a DeliveryTarget.
 * Handles: "owner", "default", "room:<uuid>", "a2a:<hubAddr>", "context",
 * "agentvault:room:<uuid>" (channel-prefixed), "agent:<name>" (→ a2a).
 */
export function parseTarget(raw: string): DeliveryTarget {
  // Strip channel prefix: "agentvault:room:X" → "room:X"
  let target = raw;
  if (target.startsWith("agentvault:")) {
    target = target.slice("agentvault:".length);
  }

  // Map "agent:X" → "a2a:X" (OpenClaw uses "agent:", plugin uses "a2a:")
  if (target.startsWith("agent:")) {
    target = "a2a:" + target.slice("agent:".length);
  }

  if (target === "owner" || target === "default") {
    return { kind: "owner" };
  }
  if (target === "context") {
    return { kind: "context" };
  }
  if (target.startsWith("room:")) {
    const roomId = target.slice("room:".length);
    if (!roomId) throw new Error(`Invalid room target: "${raw}" — missing room ID`);
    return { kind: "room", roomId };
  }
  if (target.startsWith("a2a:")) {
    const hubAddress = target.slice("a2a:".length);
    if (!hubAddress) throw new Error(`Invalid A2A target: "${raw}" — missing hub address`);
    return { kind: "a2a", hubAddress };
  }
  if (target.startsWith("a2a_channel:") || target.startsWith("channel:")) {
    const channelId = target.replace(/^(a2a_)?channel:/, "");
    if (!channelId) throw new Error(`Invalid A2A channel target: "${raw}" — missing channel ID`);
    return { kind: "a2a_channel", channelId };
  }

  throw new Error(`Unrecognized delivery target: "${raw}". Use "owner", "room:<id>", "a2a:<addr>", "channel:<id>", or "context".`);
}

export interface TrustTokenState {
  token: string | null;
  tier: string | null;
  expiresAt: string | null;
}
